import classNames from 'classnames'
import React, { useState, useRef } from 'react'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import Link from '@/components/Common/Link'
import { GA4Event } from '@/root/utils/common/web/gtm-events/types'
import { useAnalyticsEvent } from '@/hooks/analytics/use-analytics-event'
import { yieldToMain } from '@/root/utils/yield-to-main'

export const Table = ({
  table,
  sliceLength = table.tableContent.length,
  containerClassName = '',
  expended_label = 'See less',
  collapsed_label = 'See all',
  variant = 'primary',
  eventData,
  firstColumnClasses = '',
  rowClassName = '',
}: {
  table: {
    tableHeading?: Array<string | JSX.Element>
    tableContent: Array<{
      columns?: Array<JSX.Element | string | number>
      subrows?: Array<Array<JSX.Element | string | number>>
      url?: string
      noIndexUrl?: boolean
      backgroundColor?: string
      anchorTitle?: string | number
    }>
    innerBorder?: boolean
    withoutOuterBorder?: boolean
    tableTitle?: string
    tableSubTitle?: string
  }
  sliceLength?: number
  containerClassName?: string
  expended_label?: string
  collapsed_label?: string
  variant?: 'primary' | 'secondary'
  eventData?: GA4Event
  firstColumnClasses?: string
  rowClassName?: string
}) => {
  const [toggle, setToggle] = useState(false)
  const [expandedSubRows, setExpandedSubRows] = useState<number[]>([])
  const ref = useRef<HTMLDivElement | null>(null)
  const pushEvent = useAnalyticsEvent()

  const TableCell = (
    data,
    columnIndex,
    row,
    isSubRow = false,
    rowIndex = -1,
  ) => {
    const { props } = data as JSX.Element
    return (
      <td
        className={classNames(
          'break-words text-sm text-brand-black group-hover:bg-brand-tertiary-grey',
          { 'border-r': table.innerBorder },
          { 'min-w-[100px] text-right': columnIndex !== 0 },
          { 'sticky left-0 bg-white lg:min-w-[150px]': columnIndex == 0 },
          { 'px-2 py-2.5': !row.url || row.noIndexUrl },
          { rowClassName },
        )}
        key={columnIndex}
        style={{ backgroundColor: row.backgroundColor }}
      >
        {row.url && columnIndex === 0 && !row.noIndexUrl ? (
          <Link
            href={row.url}
            className={classNames('block w-full px-2 py-2.5', rowClassName, {
              'whitespace-nowrap': props?.data?.trait === 'button',
            })}
            title={row.anchorTitle}
            onClick={() => {
              if (eventData) {
                pushEvent({
                  gtm4Payload: {
                    eventData: {
                      ...eventData,
                      eventLabel: row.anchorTitle,
                    },
                  },
                })
              }
            }}
          >
            {data}
          </Link>
        ) : !isSubRow && columnIndex === 0 && row.subrows?.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-1">
            {data}
            <button
              className="cursor-pointer text-base font-medium leading-6"
              aria-expanded={expandedSubRows.includes(rowIndex)}
              aria-label={
                expandedSubRows.includes(rowIndex)
                  ? 'Collapse subrows'
                  : 'Expand subrows'
              }
              onClick={() => {
                setExpandedSubRows(prevState => {
                  if (prevState.includes(rowIndex)) {
                    return prevState.filter(index => index !== rowIndex)
                  }
                  return [...prevState, rowIndex]
                })
              }}
            >
              {expandedSubRows.includes(rowIndex) ? '-' : '+'}
            </button>
          </div>
        ) : (
          data
        )}
      </td>
    )
  }

  const TableRow = (columns, rowIndex, row, isSubRow = false) => {
    return (
      <tr
        key={rowIndex}
        className={classNames(
          `${containerClassName} group border-b last:border-b-0 hover:bg-brand-tertiary-grey`,
          { 'cursor-pointer': row.url },
          { 'border-b-2': isSubRow && rowIndex === row.subrows?.length - 1 },
        )}
        style={{ backgroundColor: row.backgroundColor }}
        onClick={() => {
          if (row.onClick) {
            row.onClick()
          } else if (row.url) {
            // Fallback to URL navigation
            window.location.href = row.url
          }
        }}
      >
        {columns?.map((data, columnIndex) => {
          return TableCell(data, columnIndex, row, isSubRow, rowIndex)
        })}
      </tr>
    )
  }
  return (
    <div>
      <div className="no-scrollbar overflow-x-auto" ref={ref}>
        {(table.tableTitle || table.tableSubTitle) && (
          <div className="px-2 pt-3 font-medium">
            {table.tableTitle && <div>{table.tableTitle} </div>}
            {table.tableSubTitle && (
              <div className="text-13px text-brand-grey-base">
                {table.tableSubTitle}
              </div>
            )}
          </div>
        )}
        <table
          className={classNames('my-3 w-full md:my-6', {
            border: !table.withoutOuterBorder,
            'md:!my-3': variant === 'secondary',
          })}
        >
          <thead>
            <tr
              className={classNames('bg-brand-grey-background', {
                'bg-white': variant === 'secondary',
              })}
            >
              {table.tableHeading?.map((heading, index) => {
                return (
                  <th
                    className={classNames(
                      'break-words p-2 text-left text-13px font-normal text-brand-grey-base',
                      {
                        'border-r': table.innerBorder,
                        'text-right': index !== 0,
                        'sticky left-0 min-w-[140px] bg-brand-grey-background':
                          index == 0,
                        [firstColumnClasses]: firstColumnClasses && index === 0,
                        'bg-white font-semibold !text-brand-black':
                          variant === 'secondary',
                      },
                    )}
                    key={index}
                  >
                    {heading}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {table.tableContent
              .slice(0, toggle ? table.tableContent.length : sliceLength)
              .map((row, rowIndex) => {
                return (
                  <React.Fragment key={rowIndex}>
                    {TableRow(row.columns, rowIndex, row, false)}
                    {expandedSubRows.includes(rowIndex) &&
                      row.subrows?.map((subcolumns, subRowIndex) => {
                        return TableRow(subcolumns, subRowIndex, row, true)
                      })}
                  </React.Fragment>
                )
              })}
          </tbody>
        </table>
      </div>
      {sliceLength < table.tableContent.length && (
        <div
          className="my-2 flex cursor-pointer items-center justify-center text-center font-normal text-brand-blue-link"
          onClick={async () => {
            await yieldToMain()
            setToggle(prevState => !prevState)
            if (toggle) {
              ref.current?.scrollIntoView()
            }
          }}
        >
          <span className="flex items-center">
            {toggle ? expended_label : collapsed_label}
            <ArrowDown
              width={20}
              height={20}
              className={toggle ? 'rotate-180' : ''}
            />
          </span>
        </div>
      )}
    </div>
  )
}
